import { useEffect } from 'react';
import {
  poll,
  protrakApiClient,
  usePaginatedGet,
  useProtrakApi,
  ASYNC_JOB_ACTIONS,
  useAsyncJobContext,
  client
} from '../utils';
import { generateInstanceQuery } from './instanceQuery';
import { useAsyncJobStatusPoll } from './useAsyncJobStatusPoll';
const asyncJobBase = '/asyncJobs';

export const ASYNC_JOB_TYPES_CONTRACT = {
  ExportSchema: 'ExportSchema',
  ImportSchema: 'ImportSchema',
  ExportDashboardWidget: 'ExportDashboardWidget',
  ExportRelationWidget: 'ExportRelationWidget',
  ExportDocument: 'ExportDocument',
  GenerateReport: 'GenerateReport',
  HandleInstanceAccess: 'HandleInstanceAccess',
  DeleteInstance: 'DeleteInstance',
  PromoteInstance: 'PromoteInstance',
  GenerateViewWidgetReport: 'GenerateViewWidgetReport',
  ImportUsersForRole: 'ImportUsersForRole',
  ExportUserProfile: 'ExportUserProfile',
  ImportUsers: 'ImportUsers'
};

export const ASYNC_JOB_STATUS_CONTRACT = {
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  ABANDONED: 'Abandoned'
};

export function useExportCsv(jobType) {
  const { dispatch, reload } = useAsyncJobContext();

  const onSuccess = () => {
    dispatch({
      type: ASYNC_JOB_ACTIONS.TOGGLE_NOTIFICATION_DRAWER
    });
    reload();
  };

  const { state: asyncJob, run: doCreateJob } = useProtrakApi(
    { promiseFn: postAsyncJob },
    { defer: true, onSuccess: onSuccess }
  );

  const exportCsv = ({ instanceQuery, skip, take, widgetId, instanceId }) => {
    let payload = {};
    if (jobType === ASYNC_JOB_TYPES_CONTRACT.ExportDashboardWidget) {
      payload = {
        type: jobType,
        instanceQuery: generateInstanceQuery(instanceQuery),
        skip,
        take,
        widgetId,
        instanceId
      };
    } else if (jobType === ASYNC_JOB_TYPES_CONTRACT.ExportRelationWidget) {
      payload = {
        type: jobType,
        relatedInstanceQuery: generateInstanceQuery(instanceQuery),
        skip,
        take,
        widgetId,
        instanceId
      };
    }
    doCreateJob(payload);
  };

  useEffect(() => {
    if (asyncJob.isError) {
      alert('Failed to Export');
    }
  }, [asyncJob]);

  return [asyncJob, exportCsv];
}

export function useAsyncJobs(pageSize, asyncJobType) {
  return usePaginatedGet(
    { requestConfig: getAsyncJobs, asyncJobType },
    {},
    pageSize
  );
}

export function useAsyncJobPoll(id) {
  const { state, run } = useProtrakApi({ promiseFn: getJobDetails, id: id });
  useAsyncJobStatusPoll(state, run);
}

export function useUpdateAsyncJobStatus(id) {
  const { state, run } = useProtrakApi(
    {
      requestConfig: updateAsyncJobStatus,
      id
    },
    { defer: true }
  );

  return { state, run };
}

const updateAsyncJobStatus = async ({ id, authContext }) => {
  const endpoint = `${asyncJobBase}/${id}`;
  const res = await protrakApiClient(
    endpoint,
    {
      method: 'PUT',
      data: { isRead: true }
    },
    authContext
  );
  return res;
};

export function useInstancePromoteProgress() {
  const { state, run } = useProtrakApi(
    { promiseFn: getJobDetails },
    { defer: true }
  );
  useAsyncJobStatusPoll(state, run);
  return [state, run];
}

export function useLatestReportJobData(widgetId, instanceId) {
  const { state, run } = useProtrakApi(
    {
      promiseFn: fetchReportDataForLatestJob,
      widgetId,
      instanceId
    },
    { defer: true }
  );

  return { state, run };
}

export function useChangeInstanceVisibility() {
  const { dispatch, reload } = useAsyncJobContext();
  const onSuccess = () => {
    dispatch({
      type: ASYNC_JOB_ACTIONS.TOGGLE_NOTIFICATION_DRAWER
    });
    reload();
  };

  const { state: asyncJob, run: doCreateJob } = useProtrakApi(
    { promiseFn: postAsyncJob },
    { defer: true, onSuccess: onSuccess }
  );

  const changeVisibility = ({ instanceId, instanceVisibility }) => {
    let payload = {
      type: ASYNC_JOB_TYPES_CONTRACT.HandleInstanceAccess,
      instanceId,
      instanceVisibility
    };

    doCreateJob(payload);
  };

  return [asyncJob, changeVisibility];
}

export function useDeleteInstance() {
  const { dispatch, reload } = useAsyncJobContext();
  const onSuccess = () => {
    dispatch({
      type: ASYNC_JOB_ACTIONS.TOGGLE_NOTIFICATION_DRAWER
    });
    reload();
  };

  const { state: asyncJob, run: doCreateJob } = useProtrakApi(
    { promiseFn: postAsyncJob },
    { defer: true, onSuccess: onSuccess }
  );

  const deleteInstance = ({ promiseParams }) => {
    let payload = {
      type: ASYNC_JOB_TYPES_CONTRACT.DeleteInstance,
      instanceId: promiseParams.instanceId
    };
    doCreateJob(payload);
  };

  return [asyncJob, deleteInstance];
}

export function useReportDataGenerator() {
  const { dispatch, reload } = useAsyncJobContext();

  //TODO: to avoid double polling, this needs to be refactored
  //omce refactor is done, there is no need to pass onCreateJob to
  //generateReportData also.
  //create job - open drawer which will start polling
  //once the job is complete, get file url and json content
  //in effect

  const onCreateJob = () => {
    reload();
    dispatch({
      type: ASYNC_JOB_ACTIONS.TOGGLE_NOTIFICATION_DRAWER
    });
  };

  const { state, run } = useProtrakApi(
    { promiseFn: generateReportData, onCreateJob: onCreateJob },
    {
      defer: true
    }
  );

  const generateReport = ({ widgetId, reportQuery, layout = false }) => {
    let payload = {
      type: ASYNC_JOB_TYPES_CONTRACT.GenerateReport,
      widgetId: widgetId,
      reportQuery: { ...reportQuery, layout: layout }
    };
    run(payload);
  };

  return { state, generateReport };
}

export const postAsyncJob = async ({
  jobType,
  authContext,
  ...otherParams
}) => {
  const res = await protrakApiClient(
    asyncJobBase,
    {
      method: 'POST',
      data: {
        type: jobType,
        ...otherParams
      }
    },
    authContext
  );
  return res;
};

// const updateAsyncJobStatus = ({ id }) => {
//   const endpoint = `${asyncJobBase}/${id}`;
//   return {
//     endpoint: endpoint, config: {
//       method: 'PUT',
//       data: { isRead: true }
//     }
//   };
// };

export const isJobExecutionComplete = data => {
  return data.status !== ASYNC_JOB_STATUS_CONTRACT.IN_PROGRESS;
};

const generateReportData = async ({
  widgetId,
  reportQuery,
  onCreateJob,
  authContext
}) => {
  const postState = await postAsyncJob({
    jobType: reportQuery.layout
      ? ASYNC_JOB_TYPES_CONTRACT.GenerateViewWidgetReport
      : ASYNC_JOB_TYPES_CONTRACT.GenerateReport,
    widgetId,
    reportQuery,
    authContext
  });

  onCreateJob && onCreateJob();

  const jobDetails = await pollJobDetails({ id: postState.data, authContext });
  return getReportDataFromJob({ jobDetails });
};

const fetchReportDataForLatestJob = async ({
  widgetId,
  instanceId,
  authContext
}) => {
  let jobDetails = await getLatestJobForReportWidget({
    widgetId,
    instanceId,
    authContext
  });
  if (!isJobExecutionComplete(jobDetails)) {
    jobDetails = await pollJobDetails({ id: jobDetails.id, authContext });
  }
  return getReportDataFromJob({ jobDetails });
};

const getReportDataFromJob = async ({ jobDetails }) => {
  if (jobDetails.status === ASYNC_JOB_TYPES_CONTRACT.FAILED) {
    return { data: { ...jobDetails } };
  }
  const reportJson = await loadReportJson({ fileUrl: jobDetails.url });
  return { data: { ...jobDetails, reportJson } };
};

const pollJobDetails = async ({ id, authContext }) => {
  const res = await poll(
    () => getJobDetails({ id, authContext }),
    isJobExecutionComplete,
    process.env.REACT_APP_ASYNC_JOB_POLLING_INTERVAL
  );

  return res;
};

const getAsyncJobs = ({ skip, take, asyncJobType }) => {
  return {
    endpoint: asyncJobBase,
    config: {
      params: { skip, take, asyncJobType }
    }
  };
};

const getJobDetails = async ({ id, authContext }) => {
  const endpoint = `${asyncJobBase}/${id}`;
  const res = await protrakApiClient(endpoint, {}, authContext);
  return res;
};

const getLatestJobForReportWidget = async ({
  widgetId,
  instanceId,
  authContext
}) => {
  const endpoint = `${asyncJobBase}/reportWidgets/${widgetId}?instanceId=${instanceId}`;
  const res = await protrakApiClient(endpoint, {}, authContext);
  return res.data;
};

const loadReportJson = async ({ fileUrl }) => {
  try {
    const res = await client(fileUrl);
    return res.data;
  } catch (error) {
    return error;
  }
};

const getAsyncJobStatus = ({ jobid }) => {
  const url = `${asyncJobBase}/${jobid}`;
  return {
    endpoint: url,
    config: {
      method: 'GET'
    }
  };
};

export function useAsyncJobStatus() {
  const { state, run } = useProtrakApi(
    {
      requestConfig: getAsyncJobStatus
    },
    { defer: true }
  );

  return { state, run };
}
